import {
  Component,
  Host,
  Prop,
  Element,
  h,
  Event,
  EventEmitter
} from "@stencil/core";

import { MercuryBundles } from "@genexus/mercury";
import { AccordionModel } from "@genexus/chameleon-controls-library";

import { Locale } from "../../../common/locale";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/accordion",
  "utils/elevation",
  "utils/layout",
  "utils/typography",
  "utils/spacing"
];

@Component({
  tag: "gx-ide-launchpad-apis",
  styleUrl: "launchpad-apis.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/launchpad-apis"]
})
export class GxIdeLaunchpadApis {
  #componentLocale: any;
  #ACCORDION_MODEL: AccordionModel = [];

  @Prop() apis: Array<any>;
  @Element() element: HTMLElement;

  /**
   * Callback function for object selection (passed from parent)
   */
  @Prop() onObjectSelected?: (event: any) => void;

  /**
   * Event emitted when copying to clipboard
   */
  @Event() copyToClipboard: EventEmitter<{ url: string }>;

  async componentWillLoad() {
    try {
      this.#componentLocale = await Locale.getComponentStrings(this.element);
    } catch (error) {
      console.error("Error loading locale:", error);
      this.#componentLocale = {};
    }

    this.initializeAccordionModel();
  }

  /**
   * Initialize accordion model with dynamic sections for each API
   */
  private initializeAccordionModel() {
    if (!this.apis || !Array.isArray(this.apis)) {
      this.#ACCORDION_MODEL = [];
      return;
    }

    this.#ACCORDION_MODEL = this.apis
      .map((api: any, index: number) => {
        let url: string;

        if (typeof api === "string") {
          url = api;
        } else if (api && typeof api === "object" && api.url) {
          url = api.url.toString();
        } else {
          return null;
        }

        const lastSlash = url.lastIndexOf("/");
        const name = url.substring(lastSlash + 1, url.length).toUpperCase();

        return {
          id: `api-${index}`,
          caption: name,
          expanded: true
        };
      })
      .filter(Boolean);
  }

  renderApis() {
    if (!this.apis || !Array.isArray(this.apis)) {
      return null;
    }

    return this.apis
      .map((api: any, index: number) => {
        let apiData: any;

        if (typeof api === "string") {
          apiData = { url: api };
        } else if (api && typeof api === "object" && api.url) {
          apiData = api;
        } else {
          return null;
        }

        return (
          <div
            key={`api-${index}`}
            slot={`api-${index}`}
            class="api-content spacing-body"
          >
            <gx-ide-launchpad-api
              data={apiData}
              onObjectSelected={(event: any) =>
                this.handleObjectSelection(event)
              }
              onCopyToClipboard={(event: any) =>
                this.handleCopyToClipboard(event)
              }
            ></gx-ide-launchpad-api>
          </div>
        );
      })
      .filter(Boolean);
  }

  /**
   * Handles object selection from subcomponents
   */
  private handleObjectSelection(event: any) {
    if (this.onObjectSelected) {
      this.onObjectSelected(event);
    }
  }

  /**
   * Handles copy to clipboard from subcomponents
   */
  private handleCopyToClipboard(event: any) {
    this.copyToClipboard.emit({ url: event.detail.url });
  }

  render() {
    if (!this.apis || this.apis.length === 0) {
      return (
        <Host>
          <ch-theme model={CSS_BUNDLES}></ch-theme>
          <div class="no-data">
            <p class="body-regular-s">
              {this.#componentLocale?.noApis || "No APIs available"}
            </p>
          </div>
        </Host>
      );
    }

    return (
      <Host>
        <ch-theme model={CSS_BUNDLES}></ch-theme>

        <div class="main">
          <ch-accordion-render
            class="accordion-filled-header elevation-2 apis-accordion"
            model={this.#ACCORDION_MODEL}
          >
            {this.renderApis()}
          </ch-accordion-render>
        </div>
      </Host>
    );
  }
}
